import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import {
  Palette,
  Upload,
  Trash2,
  Image as ImageIcon,
  Monitor,
  Globe,
  Check,
  Link2
} from 'lucide-react'
import toast from 'react-hot-toast'
import { organizationsAPI } from '../../services/organizationsAPI'

const DEFAULT_PRIMARY = '#3B82F6'
const DEFAULT_SECONDARY = '#64748B'
const HEX = /^#([0-9A-Fa-f]{6})$/
const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif,image/x-icon,.ico'
const MAX_BYTES = 5 * 1024 * 1024

const normalizeHex = (value, fallback) => {
  if (!value) return fallback
  const next = value.startsWith('#') ? value : `#${value}`
  return HEX.test(next) ? next.toUpperCase() : fallback
}

function DropZone({ label, hint, previewUrl, file, compact = false, disabled, onFile, onClear }) {
  const [dragOver, setDragOver] = useState(false)

  const takeFile = (picked) => {
    if (disabled || !picked) return
    if (picked.size > MAX_BYTES) {
      toast.error('Image must be 5MB or smaller')
      return
    }
    onFile(picked)
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-sm font-medium text-secondary-800">{label}</label>
        {previewUrl && !disabled && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center text-xs font-medium text-red-600 hover:text-red-700"
          >
            <Trash2 className="mr-1 h-3 w-3" />
            Remove
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-secondary-500">{hint}</p>
      <label
        className={`relative flex flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed transition ${
          compact ? 'h-28' : 'h-40'
        } ${disabled ? 'cursor-default' : 'cursor-pointer'} ${
          dragOver
            ? 'border-primary-400 bg-primary-50'
            : previewUrl
              ? 'border-secondary-200 bg-white'
              : 'border-secondary-300 bg-secondary-50 hover:border-primary-300 hover:bg-primary-50/40'
        }`}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          takeFile(e.dataTransfer.files?.[0])
        }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain p-4" />
        ) : (
          <div className="px-4 text-center">
            <Upload className="mx-auto mb-2 h-6 w-6 text-secondary-400" />
            <p className="text-sm text-secondary-600">
              <span className="font-semibold text-primary-600">Click to upload</span> or drag and drop
            </p>
            <p className="mt-1 text-xs text-secondary-400">PNG, SVG, JPG, ICO · max 5MB</p>
          </div>
        )}
        {!disabled && (
          <input
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              takeFile(e.target.files?.[0])
              e.target.value = ''
            }}
          />
        )}
      </label>
      {file && <p className="mt-2 truncate text-xs text-secondary-500">{file.name}</p>}
    </div>
  )
}

function ColorField({ label, value, onChange, disabled }) {
  const safe = HEX.test(value) ? value : DEFAULT_PRIMARY
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-secondary-800">{label}</label>
      <div className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-white p-2">
        <label className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-secondary-200 shadow-sm">
          <span className="absolute inset-0" style={{ backgroundColor: safe }} />
          <input
            type="color"
            value={safe}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-default"
          />
        </label>
        <Input
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="border-0 font-mono uppercase shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  )
}

export default function OrganizationBrandingTab({ organization, canEdit }) {
  const queryClient = useQueryClient()
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY)
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY)
  const [logoFile, setLogoFile] = useState(null)
  const [faviconFile, setFaviconFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [faviconPreview, setFaviconPreview] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [faviconUrl, setFaviconUrl] = useState('')
  const [clearLogo, setClearLogo] = useState(false)
  const [clearFavicon, setClearFavicon] = useState(false)
  const [showUrls, setShowUrls] = useState(false)

  useEffect(() => {
    if (!organization) return
    setPrimaryColor(normalizeHex(organization.primary_color, DEFAULT_PRIMARY))
    setSecondaryColor(normalizeHex(organization.secondary_color, DEFAULT_SECONDARY))
    setLogoPreview(organization.logo_url || '')
    setFaviconPreview(organization.favicon_url || '')
    setLogoUrl(organization.logo_url || '')
    setFaviconUrl(organization.favicon_url || '')
    setLogoFile(null)
    setFaviconFile(null)
    setClearLogo(false)
    setClearFavicon(false)
  }, [organization])

  const previewFile = (file, setter) => {
    if (!file) {
      setter('')
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => setter(reader.result)
    reader.readAsDataURL(file)
  }

  const isDirty = useMemo(() => {
    if (!organization) return false
    return (
      normalizeHex(primaryColor, DEFAULT_PRIMARY) !== normalizeHex(organization.primary_color, DEFAULT_PRIMARY) ||
      normalizeHex(secondaryColor, DEFAULT_SECONDARY) !== normalizeHex(organization.secondary_color, DEFAULT_SECONDARY) ||
      !!logoFile ||
      !!faviconFile ||
      clearLogo ||
      clearFavicon ||
      logoUrl.trim() !== (organization.logo_url || '') ||
      faviconUrl.trim() !== (organization.favicon_url || '')
    )
  }, [clearFavicon, clearLogo, faviconFile, faviconUrl, logoFile, logoUrl, organization, primaryColor, secondaryColor])

  const mutation = useMutation(
    (formData) => organizationsAPI.updateBranding(organization.id, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization', organization.id])
        toast.success('Branding updated')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update branding')
      }
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!HEX.test(primaryColor) || !HEX.test(secondaryColor)) {
      toast.error('Colors must be hex values like #3B82F6')
      return
    }

    const formData = new FormData()
    formData.append('primary_color', primaryColor)
    formData.append('secondary_color', secondaryColor)

    if (logoFile) {
      formData.append('logo', logoFile)
    } else if (clearLogo) {
      formData.append('clear_logo', 'true')
    } else if (logoUrl.trim() !== (organization.logo_url || '')) {
      formData.append('logo_url', logoUrl.trim())
    }

    if (faviconFile) {
      formData.append('favicon', faviconFile)
    } else if (clearFavicon) {
      formData.append('clear_favicon', 'true')
    } else if (faviconUrl.trim() !== (organization.favicon_url || '')) {
      formData.append('favicon_url', faviconUrl.trim())
    }

    mutation.mutate(formData)
  }

  const primary = HEX.test(primaryColor) ? primaryColor : DEFAULT_PRIMARY
  const secondary = HEX.test(secondaryColor) ? secondaryColor : DEFAULT_SECONDARY

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Palette className="mr-2 h-5 w-5 text-primary-600" />
                Branding & customization
              </CardTitle>
              <CardDescription>
                Used by product apps. Uploads are stored as a URL in the existing logo and favicon fields.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ColorField label="Primary color" value={primaryColor} onChange={setPrimaryColor} disabled={!canEdit} />
                <ColorField label="Secondary color" value={secondaryColor} onChange={setSecondaryColor} disabled={!canEdit} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <ImageIcon className="mr-2 h-5 w-5 text-primary-600" />
                Logo & favicon
              </CardTitle>
              <CardDescription>Upload a file or keep using an existing hosted URL.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <DropZone
                  label="Logo"
                  hint="Shown in the product app. PNG or SVG recommended."
                  previewUrl={logoPreview}
                  file={logoFile}
                  disabled={!canEdit}
                  onFile={(file) => {
                    setLogoFile(file)
                    setClearLogo(false)
                    previewFile(file, setLogoPreview)
                  }}
                  onClear={() => {
                    setLogoFile(null)
                    setLogoPreview('')
                    setLogoUrl('')
                    setClearLogo(true)
                  }}
                />
                <DropZone
                  label="Favicon"
                  hint="Browser tab icon. Square 32×32 or 64×64."
                  previewUrl={faviconPreview}
                  file={faviconFile}
                  compact
                  disabled={!canEdit}
                  onFile={(file) => {
                    setFaviconFile(file)
                    setClearFavicon(false)
                    previewFile(file, setFaviconPreview)
                  }}
                  onClear={() => {
                    setFaviconFile(null)
                    setFaviconPreview('')
                    setFaviconUrl('')
                    setClearFavicon(true)
                  }}
                />
              </div>

              <button
                type="button"
                onClick={() => setShowUrls((v) => !v)}
                className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                <Link2 className="mr-1.5 h-4 w-4" />
                {showUrls ? 'Hide URL fields' : 'Use or edit image URLs'}
              </button>

              {showUrls && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-secondary-800">Logo URL</label>
                    <Input
                      value={logoUrl}
                      disabled={!canEdit || !!logoFile}
                      onChange={(e) => {
                        setLogoUrl(e.target.value)
                        setClearLogo(false)
                        if (!logoFile) setLogoPreview(e.target.value)
                      }}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-secondary-800">Favicon URL</label>
                    <Input
                      value={faviconUrl}
                      disabled={!canEdit || !!faviconFile}
                      onChange={(e) => {
                        setFaviconUrl(e.target.value)
                        setClearFavicon(false)
                        if (!faviconFile) setFaviconPreview(e.target.value)
                      }}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="h-fit xl:sticky xl:top-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preview</CardTitle>
              <CardDescription>How this org branding will look in a product app.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-hidden rounded-xl border border-secondary-200 bg-secondary-50">
                <div className="flex items-center justify-between border-b border-secondary-200 bg-white px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-400">App chrome</p>
                  <Monitor className="h-3.5 w-3.5 text-secondary-400" />
                </div>
                <div className="flex min-h-[180px]">
                  <div className="w-[40%] border-r border-secondary-200 bg-white p-2">
                    <div className="mb-2 flex h-10 items-center justify-center rounded-md bg-secondary-50 px-2">
                      {logoPreview ? (
                        <img src={logoPreview} alt="" className="h-7 w-auto max-w-[110px] object-contain" />
                      ) : (
                        <span className="truncate text-[11px] font-semibold text-secondary-500">{organization.name}</span>
                      )}
                    </div>
                    <div className="rounded-md px-2 py-1.5 text-[11px] font-medium text-white" style={{ backgroundColor: primary }}>
                      Home
                    </div>
                  </div>
                  <div className="flex flex-1 items-center justify-center p-3">
                    <div className="h-16 w-full rounded-lg bg-white shadow-sm" />
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-secondary-200 bg-gradient-to-br from-primary-50 to-secondary-100">
                <div className="flex items-center justify-between bg-white/70 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-400">Sign in</p>
                  <Globe className="h-3.5 w-3.5 text-secondary-400" />
                </div>
                <div className="p-4">
                  <div className="rounded-xl bg-white p-4 text-center shadow-sm">
                    {logoPreview ? (
                      <img src={logoPreview} alt="" className="mx-auto h-8 w-auto max-w-[140px] object-contain" />
                    ) : (
                      <div className="mx-auto h-8 w-8 rounded-full" style={{ backgroundColor: primary }} />
                    )}
                    <p className="mt-2 truncate text-sm font-semibold">{organization.name}</p>
                    <div className="mt-3 h-8 rounded-md text-[11px] font-semibold leading-8 text-white" style={{ backgroundColor: primary }}>
                      Continue
                    </div>
                    <div className="mx-auto mt-2 h-1.5 w-12 rounded" style={{ backgroundColor: secondary }} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {canEdit && (
        <div className="flex items-center justify-end gap-3 border-t border-secondary-200 pt-4">
          {isDirty && (
            <span className="mr-auto text-sm text-amber-700">Unsaved changes</span>
          )}
          <Button type="submit" loading={mutation.isLoading} disabled={mutation.isLoading || !isDirty}>
            {!mutation.isLoading && <Check className="mr-2 h-4 w-4" />}
            Save branding
          </Button>
        </div>
      )}
    </form>
  )
}
